"use client";
import { useRouter } from 'next/navigation';
import { createContext, Dispatch, ReactNode, SetStateAction, useState, useEffect } from "react";

interface User {
  isLoggedIn: boolean;
  username: string;
}

interface UserContextValue {
  user: User | null;
  setUser: Dispatch<SetStateAction<User | null>>;
  logout: () => void;
}

export const UserContext = createContext<UserContextValue>({} as UserContextValue);

interface UserContextProviderProps {
  children: ReactNode;
  initialUser: User | null;
}

export const UserContextProvider: React.FC<UserContextProviderProps> = ({
  children,
  initialUser,
}) => {
  const [user, setUser] = useState<User | null>(initialUser);
  const router = useRouter();

  const logout = async () => {
    try {
      const res = await fetch("/api/logout", { method: "POST" });
      if (res.ok) {
        setUser(null);
        router.push("/login"); // Use router.push instead of redirect
      }
    } catch (err) {
      console.error("Failed to log out:", err);
    }
  };

  useEffect(() => {
    const refreshUser = async () => {
      try {
        const response = await fetch('/api/session');
        const userData = await response.json();

        if (!userData.isLoggedIn) {
          setUser(null);
          router.push('/login');
        } else {
          setUser(userData);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        router.push('/login');
      }
    };

    if (!user) {
      refreshUser();
    }
  }, [user, router]);
  // console.log({user})
  return (
    <UserContext.Provider value={{ user, setUser, logout }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContextProvider;